import { makeStyles } from "makeStyles";
import { AppRoleAssignee, AppRoleWithAssignees } from "../models";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { permissionsTableStyles } from "../styles";
import { useMemo } from "react";
import { useAppConfig } from "util/AppConfig";
import { DirectLink, getAdminRoutes, RouteConfig } from "Routes";
import PermissionsAssignees from "app-roles/permissions/PermissionsAssignees";
import _ from "lodash";

const useStyles = makeStyles()((theme) => ({
  ...permissionsTableStyles(theme),
  navItemNameCell: {
    width: "300px"
  },
  permissionNameCell: {
    width: "320px"
  }
}));

interface Props {
  permissions?: AppRoleWithAssignees[];
}

const ScreenItem: React.FunctionComponent<{
  item: RouteConfig | DirectLink;
  indent?: boolean;
  findableAssignees: { [key: string]: AppRoleAssignee[] };
}> = ({ item, indent, findableAssignees }) => {
  const { classes } = useStyles();
  return (
    <TableRow key={item.navLabel}>
      <TableCell className={classes.navItemNameCell} style={{ paddingLeft: indent ? "40px" : "16px" }}>
        {item.navLabel}
      </TableCell>
      <TableCell>
        <Table size="small" className={classes.tinyTable}>
          <TableBody>
            {item.authorizedPermissions?.map((p) => {
              const assignees = findableAssignees[p];
              return (
                <TableRow key={p}>
                  <TableCell className={classes.permissionNameCell}>{p}</TableCell>
                  <TableCell>
                    <details>
                      <summary>
                        {assignees?.length} Group{assignees.length > 1 ? "s" : ""} (
                        {(() => {
                          const allMembers = assignees?.flatMap((a) => a.members?.map((m) => m.id) ?? []) ?? [];
                          const uniqueMembers = new Set(allMembers);
                          return uniqueMembers.size + " unique member" + (uniqueMembers.size > 1 ? "s" : "");
                        })()}
                        ):
                      </summary>
                      <PermissionsAssignees assignees={_.orderBy(assignees, [(a) => a.name])} />
                    </details>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableCell>
    </TableRow>
  );
};

const AppAccessScreens: React.FunctionComponent<Props> = ({ permissions }) => {
  const { classes } = useStyles();
  const appConfig = useAppConfig();
  const adminRoutes = getAdminRoutes(appConfig);

  const hashmapPermissionIdentifierWithAssignees = useMemo(() => {
    const map: { [key: string]: AppRoleAssignee[] } = {};
    permissions?.forEach((p) => {
      map[p.identifier] = p.assignees;
    });
    return map;
  }, [permissions]);

  return (
    <TableContainer component={Paper} className={classes.topLevelTableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.navItemNameCell}>Admin Nav Item</TableCell>
            <TableCell>Accessible by Permission</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {adminRoutes?.map((item) => {
            if ("path" in item) {
              return <ScreenItem item={item} findableAssignees={hashmapPermissionIdentifierWithAssignees} />;
            } else {
              return (
                <>
                  <TableRow key={item.categoryName}>
                    <TableCell style={{ textTransform: "uppercase" }}>{item.categoryName}:</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  {item.navItems?.map((subItem) => (
                    <ScreenItem item={subItem} indent findableAssignees={hashmapPermissionIdentifierWithAssignees} />
                  ))}
                </>
              );
            }
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AppAccessScreens;
